import { StarIcon } from '@phosphor-icons/react';
import React from 'react';

import { MediaSlot } from './MediaSlot';
import { PLATFORM_SKINS, type PlatformSkin } from './platform-tokens';
import { useResolvedDarkMode } from './useResolvedDarkMode';

import { cn } from '@/lib/utils';

/**
 * Google SHOPPING AD replica — the horizontally-scrolling product carousel
 * Google serves on Search/Shopping results.
 *
 * ── PROVENANCE ────────────────────────────────────────────────────────────
 * Structure and skin come from Figma `Dl0gE7lH27N7x7OJb0DFBw`, frames
 * `28:13044` (light) and `28:13043` (dark), read via `get_metadata` 2026-08-21.
 * Outer frame 393×263, 16px padding. Inside it a row 361×201 with a 6px
 * inset holds three product cards, each 113×189, at x=6/124/242 (an 11px
 * gap between cards — the real strip scrolls past three). Each card is an
 * 113×83 product image, then a text block 10px below it (8px side padding,
 * 15px line height, 4px between lines): title, price, merchant name,
 * shipping line, a 5-star row of 10×10 glyphs at 14px pitch.
 *
 * Colours come from `PLATFORM_SKINS['google-shopping']`, already ported into
 * `--platform-google-shopping-*` in `src/index.css`. Per that file's header,
 * dark mode has no `linkText` — the blue role moves to `merchantText`
 * instead — so the title falls back to `primaryText` in dark exactly like
 * `GoogleRsaAdPreview`'s headline does for the same reason.
 *
 * ── WHAT IS DELIBERATELY *NOT* FROM FIGMA ─────────────────────────────────
 * The Figma frame's own star ratings and "Free Shipping" line are fixed
 * sample content, and the frame itself prints a caption admitting it: `"Free
 * Shipping" & star ratings are displayed for preview purposes`. Reproducing
 * decorative engagement data is exactly what this program's honesty rule
 * forbids (see `engagementTruth.test.tsx`, which every other replica in this
 * directory is pinned against). So `rating` and `shippingNote` render only
 * when the caller supplies a real value — omitted entirely otherwise, never
 * defaulted to Figma's sample — and Figma's own disclaimer caption is
 * dropped rather than ported, because the omission behaviour already IS the
 * honest disclosure. A rating of `0` is real data and still renders (five
 * empty stars), matching the family's "including zero" contract.
 *
 * ── STAR GLYPH CHOICE ──────────────────────────────────────────────────────
 * The filled star is `skin.star` and the empty one is `skin.cardBorder` —
 * both measured off the Figma renders (#e7711b / #e67624 filled, #dfe1e5 /
 * #979797 empty) rather than guessed. It briefly used `merchantText`, which
 * is GREEN in light mode: a plausible-looking colour that changes what the
 * row means. A rating is not a merchant.
 *
 * `PlatformIcons.tsx`'s header explains why THAT file draws Facebook's
 * thumbs-up, Instagram's heart, etc. as exact-vector facsimiles instead of
 * Phosphor stand-ins: those glyphs are the platform's own recognizable brand
 * marks, and approximating them defeats the point of a platform-accurate
 * preview. A five-star rating row is not a Google brand mark — it is a
 * generic rating affordance Google licenses from the same "empty/half/full
 * star" convention every review surface uses — so Phosphor's `StarIcon`
 * (`weight="fill"` for an earned star, `weight="regular"` for an unearned
 * one) is the right call, not a shortcut. No star glyph exists in
 * `PlatformIcons.tsx` and none is added there for this.
 */

export interface GoogleShoppingAdProduct {
  title: string;
  price: string;
  merchantName: string;
  imageUrl?: string | null;
  /**
   * Real star rating, 0–5. Omit entirely when there is no real rating to
   * show — do NOT default to Figma's sample stars. `0` is real data and
   * still renders (see `engagementTruth.test.tsx`'s "including zero" rule).
   */
  rating?: number;
  /** Number of ratings behind `rating`. Only meaningful alongside `rating`. */
  ratingCount?: number;
  /** e.g. "Free shipping". Omit when there is no real shipping claim to show. */
  shippingNote?: string;
}

export interface GoogleShoppingAdPreviewProps {
  products: GoogleShoppingAdProduct[];
  /**
   * Forces which Google skin variant renders, independent of Mora's active
   * theme. Omit to follow Mora's theme (the normal, real-app path); pass
   * explicitly for the style guide's side-by-side light/dark comparison and
   * for tests that must render a deterministic variant. Same contract as
   * `GoogleRsaAdPreview`/`MetaAdPreview`/`InstagramAdPreview`/`LinkedInAdPreview`.
   */
  theme?: 'light' | 'dark';
  className?: string;
}

/** The Figma frame's own width (`28:13044`/`28:13043` are both 393px). */
const GOOGLE_SHOPPING_AD_MAX_WIDTH = 393;
/** Card 113×83 image over a 113px-wide card, per the measured frame. */
const IMAGE_RATIO = 113 / 83;
const STAR_COUNT = 5;
const STAR_SIZE = 10;

const StarRow: React.FC<{ rating: number; ratingCount?: number; skin: PlatformSkin }> = ({
  rating,
  ratingCount,
  skin,
}) => {
  // Clamp + round rather than trust the caller's float verbatim — a rating
  // is always presented as whole stars in this format.
  const filled = Math.max(0, Math.min(STAR_COUNT, Math.round(rating)));

  return (
    <div
      className="flex items-center gap-1"
      role="img"
      aria-label={`${rating} out of ${STAR_COUNT} stars${
        ratingCount != null ? `, ${ratingCount} ratings` : ''
      }`}
    >
      {Array.from({ length: STAR_COUNT }, (_, index) => (
        <StarIcon
          key={index}
          size={STAR_SIZE}
          weight={index < filled ? 'fill' : 'regular'}
          style={{ color: index < filled ? (skin.star ?? skin.secondaryText) : skin.cardBorder }}
          aria-hidden="true"
        />
      ))}
      {ratingCount != null ? (
        <span className="text-micro" style={{ color: skin.secondaryText }}>
          ({ratingCount})
        </span>
      ) : null}
    </div>
  );
};

const ProductCard: React.FC<{ product: GoogleShoppingAdProduct; skin: PlatformSkin }> = ({
  product,
  skin,
}) => (
  <div
    className="w-[113px] shrink-0 overflow-hidden rounded-md border"
    style={{ backgroundColor: skin.cardBackground, borderColor: skin.cardBorder }}
  >
    {/*
      `MediaSlot` over `MissingCreativeBand`: the band exists for copy-only
      AD UNITS that have no creative field at all (Meta/LinkedIn), sized to
      reserve a wide 1.91:1 footprint for the ad the caller is drafting.
      A shopping card's image is a normal single-image slot at this
      component's own fixed 113×83 footprint, which is exactly what
      `MediaSlot` already renders — including its own honest "Add media to
      preview" empty state when `imageUrl` is absent, so no second empty
      state needed to be invented here.
    */}
    <MediaSlot ratio={IMAGE_RATIO} imageUrl={product.imageUrl} alt={product.title} />
    <div className="flex flex-col gap-1 px-2 pb-2 pt-2.5">
      <p
        className="truncate text-detail leading-snug"
        style={{ color: skin.linkText ?? skin.primaryText }}
      >
        {product.title}
      </p>
      <p className="truncate text-detail leading-snug" style={{ color: skin.primaryText }}>
        {product.price}
      </p>
      <p
        className="truncate text-detail leading-snug"
        style={{ color: skin.merchantText ?? skin.secondaryText }}
      >
        {product.merchantName}
      </p>
      {product.shippingNote ? (
        <p className="truncate text-detail leading-snug" style={{ color: skin.secondaryText }}>
          {product.shippingNote}
        </p>
      ) : null}
      {product.rating != null ? (
        <StarRow rating={product.rating} ratingCount={product.ratingCount} skin={skin} />
      ) : null}
    </div>
  </div>
);

export const GoogleShoppingAdPreview: React.FC<GoogleShoppingAdPreviewProps> = ({
  products,
  theme,
  className,
}) => {
  const isDarkMode = useResolvedDarkMode();
  const resolvedTheme = theme ?? (isDarkMode ? 'dark' : 'light');
  const skin = PLATFORM_SKINS['google-shopping'][resolvedTheme];

  return (
    <div
      className={cn('mx-auto w-full', className)}
      style={{ maxWidth: `${GOOGLE_SHOPPING_AD_MAX_WIDTH}px` }}
    >
      <div
        className="w-full p-4"
        data-platform="google-shopping-ad"
        style={{ backgroundColor: skin.pageBackground }}
      >
        {products.length > 0 ? (
          <div className="flex gap-[11px] overflow-x-auto p-1.5">
            {products.map((product, index) => (
              <ProductCard key={index} product={product} skin={skin} />
            ))}
          </div>
        ) : (
          // Honest empty state — no fabricated placeholder cards. Matches
          // `GoogleRsaAdPreview`'s "No headlines yet" / "No descriptions yet"
          // treatment for an empty asset list.
          <p className="p-1.5 text-detail" style={{ color: skin.secondaryText }}>
            No products yet
          </p>
        )}
      </div>
    </div>
  );
};
